import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { formatDateDE, getReadingMinutes, type BlogPost } from "@/lib/blog";

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

interface Props {
  post: BlogPost;
  compact?: boolean;
}

const BlogCard = ({ post, compact = false }: Props) => {
  const minutes = getReadingMinutes(post.body);
  return (
    <motion.div variants={fadeInUp}>
      <Link to={`/blog/${post.slug}`} className="block h-full group">
        <Card className="overflow-hidden h-full flex flex-col hover:shadow-lg transition-shadow">
          {post.coverImage && (
            <div className={`overflow-hidden bg-muted ${compact ? "aspect-[16/9]" : "aspect-[16/10]"}`}>
              <img
                src={post.coverImage}
                alt={post.coverAlt || post.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
            </div>
          )}
          <CardContent className="flex-1 flex flex-col p-6">
            <span className="inline-flex items-center text-xs font-semibold uppercase tracking-wider text-primary mb-2 w-fit">
              {post.category}
            </span>
            <h3 className="text-xl font-serif font-semibold text-foreground mb-2 leading-snug group-hover:text-primary transition-colors">
              {post.title}
            </h3>
            <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
              <span className="inline-flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                <time dateTime={post.date}>{formatDateDE(post.date)}</time>
              </span>
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {minutes} Min Lesezeit
              </span>
            </div>
            <p className="text-sm text-muted-foreground mb-4 flex-1">{post.excerpt}</p>
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-4">
                {post.tags.slice(0, 3).map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center px-2 py-0.5 rounded-full bg-surface text-[11px] text-muted-foreground"
                  >
                    #{t}
                  </span>
                ))}
              </div>
            )}
            <span className="inline-flex items-center gap-1 text-sm font-medium text-primary mt-auto">
              Artikel lesen
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </span>
          </CardContent>
        </Card>
      </Link>
    </motion.div>
  );
};

export default BlogCard;
